/**
 * FinanceApprovalPage
 * 
 * Página de aprovação financeira com análise de viabilidade e impacto no fluxo de caixa.
 * Permite executar análise IA e aprovar/rejeitar requisições financeiramente.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, Loader2, Sparkles,
  BarChart3,
  Check, X, BrainCircuit, AlertTriangle,
  Calendar as CalendarIcon, User, Package, Building2,
  Wallet, CreditCard, PieChart, Activity,
  Info, AlertCircle, CheckCircle2, Plus, Edit2
} from 'lucide-react';
import { financeService, purchaseService } from '../../../services/purchasesApi';
import { supplierService } from '../../../services/erpApi';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal';
import InstallmentsPreview from '../../../components/ERP/Compras/InstallmentsPreview';
import { CalendarDatePicker } from '../../../components/ERP/Calendar/Calendar';
import { toDateInputValue } from '../../../utils/dateUtils';
import './FinanceApprovalPage.css';

const FinanceApprovalPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast, showToast, hideToast } = useToast();
  
  const [requisition, setRequisition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [financialAnalysis, setFinancialAnalysis] = useState(null);
  const [useLocalAnalysis, setUseLocalAnalysis] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [useProjectedBalance, setUseProjectedBalance] = useState(false);
  const [cashBalance, setCashBalance] = useState({ current: 0, projected: 0 });
  
  // Estados do modal de forma de pagamento
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [installments, setInstallments] = useState(1);
  const [firstDueDate, setFirstDueDate] = useState('');
  const [intervalDays, setIntervalDays] = useState(30);
  const [calculating, setCalculating] = useState(false);
  const [installmentsPreview, setInstallmentsPreview] = useState(null);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [editedPayments, setEditedPayments] = useState([]);
  const [cashDueDate, setCashDueDate] = useState('');
  const [savingPaymentDates, setSavingPaymentDates] = useState(false);
  
  // Estados do modal de fornecedor
  const [supplierData, setSupplierData] = useState({ name: '', cnpj: '' });
  const [searchingSupplier, setSearchingSupplier] = useState(false);
  const [foundSupplier, setFoundSupplier] = useState(null);
  
  // Estado do switch para criar auditoria automaticamente
  const [createAuditOnApproval, setCreateAuditOnApproval] = useState(false);

  // Carregar saldo de caixa
  const loadCashBalance = React.useCallback(async () => {
    try {
      const response = await financeService.getCashBalance(useProjectedBalance);
      if (response.success) {
        setCashBalance(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar saldo de caixa:', error);
    }
  }, [useProjectedBalance]);

  // Inicializar dados do modal de pagamento quando abrir
  useEffect(() => {
    if (showPaymentModal && requisition) {
      if (requisition.payment_method) {
        setPaymentMethod(requisition.payment_method);
        setInstallments(requisition.payment_installments || 1);
        setFirstDueDate(toDateInputValue(requisition.payment_first_due_date));
        setIntervalDays(requisition.payment_interval_days || 30);
      } else {
        setPaymentMethod('cash');
        setInstallments(1);
        setFirstDueDate('');
        setIntervalDays(30);
        setInstallmentsPreview(null);
      }
    }
  }, [showPaymentModal, requisition]);

  // Sincronizar parcelas editáveis e data à vista
  useEffect(() => {
    if (!requisition) return;

    if (requisition.payment_method === 'installments' && Array.isArray(requisition.payments)) {
      setEditedPayments(requisition.payments.map((payment) => ({
        ...payment,
        due_date: toDateInputValue(payment.due_date)
      })));
    } else {
      setEditedPayments([]);
    }

    if (requisition.payment_method === 'cash') {
      setCashDueDate(toDateInputValue(requisition.payment_first_due_date));
    } else {
      setCashDueDate('');
    }
  }, [requisition]);

  // Inicializar dados do modal de fornecedor quando abrir
  useEffect(() => {
    if (showSupplierModal) {
      if (requisition?.supplier_id) {
        setSupplierData({
          name: requisition.supplier_name || '',
          cnpj: requisition.supplier_cpf_cnpj || ''
        });
        setFoundSupplier({
          id: requisition.supplier_id,
          name: requisition.supplier_name,
          cpf_cnpj: requisition.supplier_cpf_cnpj
        });
      } else {
        setSupplierData({ name: '', cnpj: '' });
        setFoundSupplier(null);
      }
    }
  }, [showSupplierModal, requisition]);

  // Carregar requisição
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timeoutId = null;

    const loadRequisition = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Timeout de segurança (30 segundos)
        timeoutId = setTimeout(() => {
          if (!cancelled) {
            setLoading(false);
            setError('Tempo de carregamento excedido. Tente novamente.');
          }
        }, 30000);
        
        const response = await financeService.getById(id);
        
        clearTimeout(timeoutId);
        
        if (cancelled) return;
        
        if (response && response.success) {
          setRequisition(response.data);
          // Verificar se já existe análise financeira
          if (response.data?.financial_analysis) {
            setFinancialAnalysis(response.data.financial_analysis);
            setShowAnalysis(true);
          }
          // Carregar saldo de caixa
          loadCashBalance();
        } else {
          const errorMessage = response?.message || 'Erro ao carregar requisição';
          setError(errorMessage);
          if (showToast) {
            showToast(errorMessage, 'error');
          }
        }
      } catch (err) {
        if (timeoutId) clearTimeout(timeoutId);
        if (cancelled) return;
        
        const errorMessage = err.response?.data?.message || err.message || 'Erro ao carregar requisição';
        setError(errorMessage);
        if (showToast) {
          showToast(errorMessage, 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadRequisition();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loadCashBalance]); // Removido showToast da dependência

  // Logs para debug - DEVE estar antes de qualquer retorno condicional
  useEffect(() => {
    console.log('[FinanceApprovalPage] ========== DEBUG LOGS ==========');
    console.log('[FinanceApprovalPage] Toast state:', toast);
    console.log('[FinanceApprovalPage] Toast message type:', typeof toast?.message);
    console.log('[FinanceApprovalPage] Toast message value:', toast?.message);
    if (toast?.message && typeof toast.message !== 'string') {
      console.error('[FinanceApprovalPage] ❌ Toast message is not a string!', toast.message);
      console.error('[FinanceApprovalPage] Toast message keys:', Object.keys(toast.message || {}));
    }
  }, [toast]);

  useEffect(() => {
    console.log('[FinanceApprovalPage] Requisition:', requisition);
    console.log('[FinanceApprovalPage] Financial Analysis:', financialAnalysis);
    if (financialAnalysis?.analysis && typeof financialAnalysis.analysis !== 'string') {
      console.log('[FinanceApprovalPage] Financial Analysis.analysis type:', typeof financialAnalysis.analysis);
      console.log('[FinanceApprovalPage] Financial Analysis.analysis value:', financialAnalysis.analysis);
    }
    if (financialAnalysis?.reasoning && typeof financialAnalysis.reasoning !== 'string') {
      console.warn('[FinanceApprovalPage] ⚠️ Financial Analysis.reasoning is not a string!', financialAnalysis.reasoning);
    }
    if (financialAnalysis?.recommendations) {
      financialAnalysis.recommendations.forEach((rec, idx) => {
        if (typeof rec !== 'string') {
          console.warn(`[FinanceApprovalPage] ⚠️ Recommendation ${idx} is not a string!`, rec);
        }
      });
    }
    if (financialAnalysis?.metrics) {
      Object.keys(financialAnalysis.metrics).forEach(key => {
        const value = financialAnalysis.metrics[key];
        if (value && typeof value !== 'string' && typeof value !== 'number') {
          console.warn(`[FinanceApprovalPage] ⚠️ Metric ${key} is not a string/number!`, value);
        }
      });
    }
    console.log('[FinanceApprovalPage] =================================');
  }, [requisition, financialAnalysis]);

  const getPriorityConfig = (priority) => {
    const configs = {
      critical: { label: 'Crítico', className: 'priority-critical' },
      urgent: { label: 'Urgente', className: 'priority-urgent' },
      normal: { label: 'Normal', className: 'priority-normal' },
      low: { label: 'Baixa', className: 'priority-low' }
    };
    return configs[priority] || configs.normal;
  };

  const getCategoryConfig = (category) => {
    const cat = category || 'opex';
    const configs = {
      opex: { label: 'OPEX', className: 'category-opex', icon: Package },
      capex: { label: 'CAPEX', className: 'category-capex', icon: Building2 }
    };
    return configs[cat.toLowerCase()] || configs.opex;
  };

  // Salvar forma de pagamento
  const handleSavePaymentMethod = async () => {
    const newErrors = {};

    if (paymentMethod === 'installments') {
      if (!installments || installments < 1 || installments > 12) {
        newErrors.installments = 'Número de parcelas deve estar entre 1 e 12';
      }
      if (!firstDueDate) {
        newErrors.firstDueDate = 'Data do primeiro vencimento é obrigatória';
      }
      if (!intervalDays || intervalDays < 7 || intervalDays > 90) {
        newErrors.intervalDays = 'Intervalo deve estar entre 7 e 90 dias';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setPaymentErrors(newErrors);
      return;
    }

    const paymentData = {
      payment_method: paymentMethod,
      payment_installments: paymentMethod === 'installments' ? installments : null,
      payment_first_due_date: paymentMethod === 'installments' ? firstDueDate : null,
      payment_interval_days: paymentMethod === 'installments' ? intervalDays : null
    };

    try {
      const response = await financeService.updatePaymentMethod(id, paymentData);
      if (response.success) {
        showToast('Forma de pagamento atualizada com sucesso', 'success');
        // Recarregar requisição
        const reloadResponse = await financeService.getById(id);
        if (reloadResponse.success) {
          setRequisition(reloadResponse.data);
        }
        setShowPaymentModal(false);
      } else {
        showToast(response.message || 'Erro ao atualizar forma de pagamento', 'error');
      }
    } catch (error) {
      console.error('Erro ao atualizar forma de pagamento:', error);
      showToast('Erro ao atualizar forma de pagamento', 'error');
    }
  };

  // Formatar CNPJ/CPF
  const formatCnpj = (value) => {
    const clean = value.replace(/\D/g, '');
    if (clean.length <= 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4').replace(/(\d{3})(\d{3})(\d)/, '$1.$2.$3').replace(/(\d{3})(\d)/, '$1-$2');
    } else {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5').replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2.$3').replace(/(\d{2})(\d{3})/, '$1.$2');
    }
  };

  // Buscar fornecedor por CNPJ
  const handleCnpjChange = async (cnpj) => {
    const cleanCnpj = cnpj.replace(/\D/g, '');
    const formatted = formatCnpj(cnpj);
    setSupplierData({ ...supplierData, cnpj: formatted });

    if (cleanCnpj.length >= 11) {
      setSearchingSupplier(true);
      setFoundSupplier(null);
      
      try {
        const response = await supplierService.findByCnpj(cleanCnpj);
        if (response.success && response.data) {
          setFoundSupplier(response.data);
          setSupplierData({
            name: response.data.name,
            cnpj: formatted
          });
          showToast('Fornecedor encontrado e vinculado automaticamente', 'success');
        } else {
          setFoundSupplier(null);
        }
      } catch (err) {
        console.error('Erro ao buscar fornecedor:', err);
        setFoundSupplier(null);
      } finally {
        setSearchingSupplier(false);
      }
    } else {
      setFoundSupplier(null);
    }
  };

  // Salvar fornecedor
  const handleSaveSupplier = async () => {
    if (!supplierData.name) {
      showToast('Nome do fornecedor é obrigatório', 'error');
      return;
    }

    try {
      let finalSupplierId = foundSupplier?.id;
      
      if (!finalSupplierId) {
        const createResponse = await supplierService.create({
          name: supplierData.name,
          cpfCnpj: supplierData.cnpj.replace(/\D/g, '')
        });
        if (createResponse.success && createResponse.data?.id) {
          finalSupplierId = createResponse.data.id;
        } else {
          showToast(createResponse.message || 'Erro ao criar fornecedor', 'error');
          return;
        }
      }

      // Validar que temos um ID válido antes de atualizar
      if (!finalSupplierId) {
        showToast('Erro: ID do fornecedor não encontrado', 'error');
        return;
      }

      const updateResponse = await purchaseService.update(id, {
        supplier_id: finalSupplierId
      });

      if (updateResponse.success) {
        showToast(
          foundSupplier 
            ? 'Fornecedor vinculado com sucesso' 
            : 'Fornecedor cadastrado e vinculado com sucesso',
          'success'
        );
        // Recarregar requisição
        const reloadResponse = await financeService.getById(id);
        if (reloadResponse.success) {
          setRequisition(reloadResponse.data);
        }
        setShowSupplierModal(false);
      } else {
        showToast(updateResponse.message || 'Erro ao vincular fornecedor', 'error');
      }
    } catch (error) {
      console.error('Erro ao atualizar fornecedor:', error);
      showToast(error.message || 'Erro ao atualizar fornecedor', 'error');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getTotalValue = () => {
    // Prioriza invoice_total se existir e for maior que 0, senão usa total_value
    return requisition?.invoice_total && requisition.invoice_total > 0
      ? requisition.invoice_total
      : requisition?.total_value || 0;
  };

  // Obter data mínima (hoje)
  const getMinDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const hasPaymentDateChanges = React.useMemo(() => {
    if (!requisition?.payment_method) return false;

    if (requisition.payment_method === 'cash') {
      return cashDueDate !== toDateInputValue(requisition.payment_first_due_date);
    }

    if (requisition.payment_method === 'installments') {
      return editedPayments.some((payment) => {
        const original = requisition.payments?.find((p) => p.id === payment.id);
        if (!original) return false;
        return payment.due_date !== toDateInputValue(original.due_date);
      });
    }

    return false;
  }, [requisition, cashDueDate, editedPayments]);

  const handleInstallmentDateChange = (paymentId, newDate) => {
    setEditedPayments((prev) => prev.map((payment) => (
      payment.id === paymentId ? { ...payment, due_date: newDate } : payment
    )));
  };

  const handleSavePaymentDates = async () => {
    if (!requisition?.payment_method) {
      showToast('Defina a forma de pagamento antes de salvar as datas', 'error');
      return;
    }

    setSavingPaymentDates(true);
    try {
      const payload = requisition.payment_method === 'cash'
        ? { payment_first_due_date: cashDueDate }
        : {
            payments: editedPayments.map((payment) => ({
              id: payment.id,
              due_date: payment.due_date
            }))
          };

      const response = await financeService.updatePaymentDates(id, payload);
      if (response.success) {
        showToast('Datas de vencimento atualizadas com sucesso', 'success');
        const reloadResponse = await financeService.getById(id);
        if (reloadResponse.success) {
          setRequisition(reloadResponse.data);
        }
      } else {
        showToast(response.message || 'Erro ao atualizar datas', 'error');
      }
    } catch (error) {
      console.error('Erro ao atualizar datas de vencimento:', error);
      showToast(error.response?.data?.message || 'Erro ao atualizar datas de vencimento', 'error');
    } finally {
      setSavingPaymentDates(false);
    }
  };

  // Calcular preview de parcelas
  const handleCalculateInstallments = async () => {
    if (paymentMethod !== 'installments' || !firstDueDate) {
      setInstallmentsPreview(null);
      return;
    }

    setCalculating(true);
    setPaymentErrors({});

    try {
      const response = await financeService.calculateInstallments(id, {
        installments,
        firstDueDate,
        intervalDays
      });

      if (response.success) {
        setInstallmentsPreview(response.data);
      } else {
        setPaymentErrors({ general: response.message || 'Erro ao calcular parcelas' });
      }
    } catch (error) {
      console.error('Erro ao calcular parcelas:', error);
      setPaymentErrors({ general: 'Erro ao calcular parcelas' });
    } finally {
      setCalculating(false);
    }
  };

  // Calcular automaticamente quando mudar dados relevantes
  useEffect(() => {
    if (showPaymentModal && paymentMethod === 'installments' && firstDueDate && installments > 0) {
      const timeoutId = setTimeout(() => {
        handleCalculateInstallments();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentModal, paymentMethod, installments, firstDueDate, intervalDays]);

  const runFinancialAnalysis = async (useLocal = false) => {
    setIsAnalyzing(true);
    try {
      const response = await financeService.analyze(id, useLocal);
      if (response.success) {
        setFinancialAnalysis(response.data);
        setShowAnalysis(true);
        showToast(
          useLocal 
            ? 'Análise financeira local concluída com sucesso!' 
            : 'Análise financeira IA concluída com sucesso!', 
          'success'
        );
      } else {
        showToast(response.message || 'Erro ao executar análise financeira', 'error');
      }
    } catch (error) {
      console.error('Erro ao executar análise financeira:', error);
      showToast(error.response?.data?.message || 'Erro ao executar análise financeira', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApprove = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Aprovar Requisição Financeiramente',
      message: `Deseja aprovar a requisição #${id}? Esta ação liberará o pagamento e avançará para o próximo estágio.`,
      type: 'success',
      onConfirm: async () => {
        await performApproval(true);
      }
    });
  };

  const handleReject = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Rejeitar Requisição Financeiramente',
      message: `Deseja rejeitar a requisição #${id}? Esta ação retornará a requisição para revisão.`,
      type: 'danger',
      onConfirm: async () => {
        await performApproval(false);
      }
    });
  };

  const performApproval = async (approved) => {
    setIsApproving(true);
    try {
      const response = await financeService.approve(id, approved, null, createAuditOnApproval && approved);
      if (response.success) {
        showToast(
          approved 
            ? 'Requisição aprovada financeiramente com sucesso!' 
            : 'Requisição rejeitada financeiramente',
          approved ? 'success' : 'info'
        );
        setTimeout(() => {
          navigate('/erp/purchases/finance');
        }, 1500);
      } else {
        showToast(response.message || 'Erro ao processar aprovação', 'error');
      }
    } catch (error) {
      console.error('Erro ao processar aprovação:', error);
      showToast(error.response?.data?.message || 'Erro ao processar aprovação', 'error');
    } finally {
      setIsApproving(false);
      setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
    }
  };

  // Calcular métricas de impacto
  const calculateImpactMetrics = () => {
    if (!requisition || !financialAnalysis) {
      return {
        totalValue: requisition?.total_value || requisition?.totalValue || 0,
        cashImpact: 0,
        monthlyImpact: 0,
        riskLevel: 'low'
      };
    }

    const totalValue = requisition.total_value || requisition.totalValue || 0;
    const cashImpact = financialAnalysis.cash_impact || totalValue;
    const monthlyImpact = financialAnalysis.monthly_impact || (totalValue / 12);
    
    let riskLevel = 'low';
    if (cashImpact > totalValue * 0.5) riskLevel = 'high';
    else if (cashImpact > totalValue * 0.2) riskLevel = 'medium';

    return {
      totalValue,
      cashImpact,
      monthlyImpact,
      riskLevel
    };
  };

  if (loading) {
    return (
      <div className="finance-approval-page">
        <div className="loading-state">
          <Loader2 className="spinner" size={48} />
          <p>Carregando requisição...</p>
        </div>
      </div>
    );
  }

  if (error || !requisition) {
    return (
      <div className="finance-approval-page">
        <div className="error-state">
          <div className="error-icon">
            <AlertTriangle size={48} />
          </div>
          <h2>Requisição Não Encontrada</h2>
          <p>{error || 'A requisição solicitada não foi encontrada'}</p>
          <button 
            className="btn-back-to-list"
            onClick={() => navigate('/erp/purchases/finance')}
          >
            Voltar para Fila
          </button>
        </div>
      </div>
    );
  }

  const priorityConfig = getPriorityConfig(requisition?.priority || 'normal');
  const categoryConfig = getCategoryConfig(requisition?.category || requisition?.financial_category);
  const CategoryIcon = categoryConfig.icon;
  const metrics = calculateImpactMetrics();
  const totalValue = requisition?.total_value || requisition?.totalValue || 0;
  const itemsCount = requisition?.items?.length || requisition?.items_count || 0;

  return (
    <div className="finance-approval-page">
      {/* Header */}
      <div className="finance-page-header-card">
        <div className="finance-header-content">
          <button 
            className="finance-btn-back-header"
            onClick={() => navigate('/erp/purchases/finance')}
          >
            <ArrowLeft size={24} />
          </button>
          <div className="finance-header-info">
            <div className="finance-header-title-row">
              <h1>#{requisition.id}</h1>
              <span className={`finance-priority-badge-header ${priorityConfig.className}`}>
                {priorityConfig.label}
              </span>
              <span className={`finance-category-badge-header ${categoryConfig.className}`}>
                {CategoryIcon && <CategoryIcon size={14} />}
                {categoryConfig.label}
              </span>
            </div>
            <p className="finance-header-subtitle">
              <DollarSign size={12} />
              {requisition.title} • Solicitado em {formatDate(requisition.created_at)}
            </p>
          </div>
        </div>

        <div className="finance-header-actions">
          {!showAnalysis ? (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button
                className="finance-btn-analyze"
                onClick={() => runFinancialAnalysis(false)}
                disabled={isAnalyzing}
                title="Análise com IA (Gemini)"
              >
                {isAnalyzing && !useLocalAnalysis ? (
                  <>
                    <Loader2 className="spinner" size={18} />
                    Analisando com IA...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Análise IA
                  </>
                )}
              </button>
              <button
                className="finance-btn-analyze-local"
                onClick={() => {
                  setUseLocalAnalysis(true);
                  runFinancialAnalysis(true);
                }}
                disabled={isAnalyzing}
                title="Análise Local (Sistema)"
              >
                {isAnalyzing && useLocalAnalysis ? (
                  <>
                    <Loader2 className="spinner" size={18} />
                    Analisando...
                  </>
                ) : (
                  <>
                    <BarChart3 size={18} />
                    Análise Local
                  </>
                )}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button
                className="finance-btn-reanalyze"
                onClick={() => {
                  setUseLocalAnalysis(false);
                  runFinancialAnalysis(false);
                }}
                disabled={isAnalyzing}
                title="Reexecutar análise com IA"
              >
                {isAnalyzing && !useLocalAnalysis ? (
                  <>
                    <Loader2 className="spinner" size={18} />
                    Reanalisando...
                  </>
                ) : (
                  <>
                    <BrainCircuit size={18} />
                    Reexecutar IA
                  </>
                )}
              </button>
              <button
                className="finance-btn-reanalyze-local"
                onClick={() => {
                  setUseLocalAnalysis(true);
                  runFinancialAnalysis(true);
                }}
                disabled={isAnalyzing}
                title="Reexecutar análise local"
              >
                {isAnalyzing && useLocalAnalysis ? (
                  <>
                    <Loader2 className="spinner" size={18} />
                    Reanalisando...
                  </>
                ) : (
                  <>
                    <BarChart3 size={18} />
                    Reexecutar Local
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="finance-content-grid">
        {/* Main Content */}
        <div className="finance-main-content">
          {/* Financial Summary Cards */}
          <div className="finance-summary-cards">
            <div className="finance-summary-card">
              <div className="finance-summary-card-icon finance-icon-primary">
                <Wallet size={20} />
              </div>
              <div className="finance-summary-card-content">
                <p className="finance-summary-label">Valor Total</p>
                <p className="finance-summary-value">{formatCurrency(totalValue)}</p>
              </div>
            </div>

            <div className="finance-summary-card">
              <div className="finance-summary-card-icon finance-icon-warning">
                <Activity size={20} />
              </div>
              <div className="finance-summary-card-content">
                <p className="finance-summary-label">Impacto no Caixa</p>
                <p className="finance-summary-value">{formatCurrency(metrics.cashImpact)}</p>
              </div>
            </div>

            <div className="finance-summary-card">
              <div className="finance-summary-card-icon finance-icon-info">
                <PieChart size={20} />
              </div>
              <div className="finance-summary-card-content">
                <p className="finance-summary-label">Impacto Mensal</p>
                <p className="finance-summary-value">{formatCurrency(metrics.monthlyImpact)}</p>
              </div>
            </div>

            <div className="finance-summary-card">
              <div className="finance-summary-card-icon finance-icon-success">
                <Package size={20} />
              </div>
              <div className="finance-summary-card-content">
                <p className="finance-summary-label">Itens</p>
                <p className="finance-summary-value">{itemsCount}</p>
              </div>
            </div>
          </div>

          {/* Financial Analysis Section */}
          {showAnalysis && financialAnalysis ? (
            <div className="finance-analysis-section">
              <div className="finance-analysis-header">
                <div className="finance-analysis-title">
                  <BrainCircuit size={20} />
                  <h3>Análise de Viabilidade Financeira</h3>
                </div>
                <span className={`finance-risk-badge finance-risk-${metrics.riskLevel}`}>
                  Risco {metrics.riskLevel === 'high' ? 'Alto' : metrics.riskLevel === 'medium' ? 'Médio' : 'Baixo'}
                </span>
              </div>

              <div className="finance-analysis-content">
                {/* Cash Flow Impact Chart */}
                <div className="finance-chart-card">
                  <h4>Impacto no Fluxo de Caixa</h4>
                  <div className="finance-chart-container">
                    <div className="finance-chart-bar">
                      <div 
                        className="finance-chart-fill"
                        style={{ 
                          width: `${Math.min(100, (metrics.cashImpact / totalValue) * 100)}%` 
                        }}
                      />
                    </div>
                    <div className="finance-chart-labels">
                      <span>R$ 0</span>
                      <span>{formatCurrency(totalValue)}</span>
                    </div>
                  </div>
                </div>

                {/* Analysis Details */}
                {financialAnalysis.analysis && (
                  <div className="finance-analysis-details">
                    <h4>Detalhes da Análise</h4>
                    <div className="finance-analysis-text">
                      {typeof financialAnalysis.analysis === 'string' ? (
                        <p>{financialAnalysis.analysis}</p>
                      ) : financialAnalysis.analysis.reasoning ? (
                        <p>{financialAnalysis.analysis.reasoning}</p>
                      ) : (
                        <div>
                          {financialAnalysis.analysis.currentCashFlow && (
                            <div style={{ marginBottom: '1rem' }}>
                              <strong>Fluxo de Caixa Atual:</strong>
                              <p>{typeof financialAnalysis.analysis.currentCashFlow === 'string' 
                                ? financialAnalysis.analysis.currentCashFlow 
                                : JSON.stringify(financialAnalysis.analysis.currentCashFlow)}</p>
                            </div>
                          )}
                          {financialAnalysis.analysis.paymentCapacity && (
                            <div style={{ marginBottom: '1rem' }}>
                              <strong>Capacidade de Pagamento:</strong>
                              <p>{typeof financialAnalysis.analysis.paymentCapacity === 'string' 
                                ? financialAnalysis.analysis.paymentCapacity 
                                : JSON.stringify(financialAnalysis.analysis.paymentCapacity)}</p>
                            </div>
                          )}
                          {financialAnalysis.analysis.risks && Array.isArray(financialAnalysis.analysis.risks) && (
                            <div style={{ marginBottom: '1rem' }}>
                              <strong>Riscos Identificados:</strong>
                              <ul>
                                {financialAnalysis.analysis.risks.map((risk, idx) => {
                                  const riskValue = typeof risk === 'string' ? risk : (risk?.message || risk?.text || JSON.stringify(risk));
                                  console.log(`[FinanceApprovalPage] Risk ${idx}:`, risk, '->', riskValue);
                                  return <li key={idx}>{riskValue}</li>;
                                })}
                              </ul>
                            </div>
                          )}
                          {financialAnalysis.analysis.opportunities && Array.isArray(financialAnalysis.analysis.opportunities) && (
                            <div>
                              <strong>Oportunidades:</strong>
                              <ul>
                                {financialAnalysis.analysis.opportunities.map((opp, idx) => {
                                  const oppValue = typeof opp === 'string' ? opp : (opp?.message || opp?.text || JSON.stringify(opp));
                                  console.log(`[FinanceApprovalPage] Opportunity ${idx}:`, opp, '->', oppValue);
                                  return <li key={idx}>{oppValue}</li>;
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Reasoning */}
                {financialAnalysis.reasoning && (
                  <div className="finance-analysis-details">
                    <h4>Raciocínio e Cálculos</h4>
                    <div className="finance-analysis-text">
                      <p>{typeof financialAnalysis.reasoning === 'string' 
                        ? financialAnalysis.reasoning 
                        : JSON.stringify(financialAnalysis.reasoning)}</p>
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {financialAnalysis.recommendations && (
                  <div className="finance-recommendations">
                    <h4>
                      <Info size={16} />
                      Recomendações
                    </h4>
                    <ul className="finance-recommendations-list">
                      {financialAnalysis.recommendations.map((rec, idx) => {
                        const recValue = typeof rec === 'string' ? rec : (rec?.message || rec?.text || JSON.stringify(rec));
                        console.log(`[FinanceApprovalPage] Recommendation ${idx}:`, rec, '->', recValue);
                        return <li key={idx}>{recValue}</li>;
                      })}
                    </ul>
                  </div>
                )}

                {/* Financial Metrics */}
                {financialAnalysis.metrics && (
                  <div className="finance-metrics-grid">
                    {financialAnalysis.metrics.payment_terms && (
                      <div className="finance-metric-item">
                        <CreditCard size={16} />
                        <div>
                          <p className="finance-metric-label">Prazo de Pagamento</p>
                          <p className="finance-metric-value">
                            {typeof financialAnalysis.metrics.payment_terms === 'string' 
                              ? financialAnalysis.metrics.payment_terms 
                              : JSON.stringify(financialAnalysis.metrics.payment_terms)}
                          </p>
                        </div>
                      </div>
                    )}
                    {financialAnalysis.metrics.cash_availability && (
                      <div className="finance-metric-item">
                        <Wallet size={16} />
                        <div>
                          <p className="finance-metric-label">Disponibilidade de Caixa</p>
                          <p className="finance-metric-value">
                            {typeof financialAnalysis.metrics.cash_availability === 'string' 
                              ? financialAnalysis.metrics.cash_availability 
                              : JSON.stringify(financialAnalysis.metrics.cash_availability)}
                          </p>
                        </div>
                      </div>
                    )}
                    {financialAnalysis.metrics.payment_forecast && (
                      <div className="finance-metric-item">
                        <CalendarIcon size={16} />
                        <div>
                          <p className="finance-metric-label">Previsão de Pagamento</p>
                          <p className="finance-metric-value">
                            {typeof financialAnalysis.metrics.payment_forecast === 'string' 
                              ? financialAnalysis.metrics.payment_forecast 
                              : JSON.stringify(financialAnalysis.metrics.payment_forecast)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="finance-no-analysis">
              <div className="finance-no-analysis-icon">
                <BarChart3 size={48} />
              </div>
              <h3>Análise Financeira Não Executada</h3>
              <p>Execute a análise de viabilidade para visualizar o impacto no fluxo de caixa e receber recomendações.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
                <button
                  className="finance-btn-analyze-large"
                  onClick={() => {
                    setUseLocalAnalysis(false);
                    runFinancialAnalysis(false);
                  }}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing && !useLocalAnalysis ? (
                    <>
                      <Loader2 className="spinner" size={20} />
                      Analisando com IA...
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} />
                      Executar Análise IA
                    </>
                  )}
                </button>
                <button
                  className="finance-btn-analyze-large-local"
                  onClick={() => {
                    setUseLocalAnalysis(true);
                    runFinancialAnalysis(true);
                  }}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing && useLocalAnalysis ? (
                    <>
                      <Loader2 className="spinner" size={20} />
                      Analisando...
                    </>
                  ) : (
                    <>
                      <BarChart3 size={20} />
                      Executar Análise Local
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Requisition Items */}
          {requisition.items && requisition.items.length > 0 && (
            <div className="finance-items-section">
              <h3>
                <Package size={16} />
                Itens da Requisição ({itemsCount})
              </h3>
              <div className="finance-items-list">
                {requisition.items.map((item, idx) => {
                  // Usar invoice_qty se disponível (quantidade do espelho), senão usar requested_qty
                  const qty = item.invoice_qty !== null && item.invoice_qty !== undefined 
                    ? item.invoice_qty 
                    : (item.requested_qty || 0);
                  
                  // Calcular valor unitário proporcionalmente baseado no total_value
                  // O total_value já prioriza invoice_total (valor extraído do espelho) quando disponível
                  let unitPrice = 0;
                  let itemTotal = 0;
                  
                  // Usar total_value que já prioriza invoice_total se disponível
                  const finalTotalValue = requisition.total_value || 0;
                  
                  if (finalTotalValue > 0) {
                    // Calcular proporcionalmente baseado na quantidade efetiva (invoice_qty ou requested_qty) e preço de custo
                    // Primeiro, calcular o total baseado em cost_price para ter a proporção
                    const totalCostPrice = requisition.items.reduce((sum, it) => {
                      const itemCostPrice = it.product_cost_price || it.cost_price || it.product_price || it.price || it.unit_price || 0;
                      // Usar invoice_qty se disponível, senão requested_qty
                      const effectiveQty = it.invoice_qty !== null && it.invoice_qty !== undefined 
                        ? it.invoice_qty 
                        : (it.requested_qty || 0);
                      return sum + (itemCostPrice * effectiveQty);
                    }, 0);
                    
                    if (totalCostPrice > 0) {
                      // Calcular proporção deste item baseado no custo
                      const itemCostPrice = item.product_cost_price || item.cost_price || item.product_price || item.price || item.unit_price || 0;
                      const itemCostTotal = itemCostPrice * qty;
                      const proportion = itemCostTotal / totalCostPrice;
                      itemTotal = finalTotalValue * proportion;
                      unitPrice = qty > 0 ? itemTotal / qty : 0;
                    } else {
                      // Se não temos cost_price, dividir igualmente pelo número de itens
                      const itemCount = requisition.items.length;
                      itemTotal = finalTotalValue / itemCount;
                      unitPrice = qty > 0 ? itemTotal / qty : 0;
                    }
                  } else {
                    // Se não temos total_value, usar cost_price diretamente
                    unitPrice = item.product_cost_price || item.cost_price || item.product_price || item.price || item.unit_price || 0;
                    itemTotal = qty * unitPrice;
                  }
                  
                  return (
                    <div key={item.id || idx} className="finance-item-card">
                      <div className="finance-item-info">
                        <p className="finance-item-name">
                          {item.product_name || item.product_name_from_catalog || 'Produto'}
                        </p>
                        <p className="finance-item-details">
                          Qtd: {qty} • 
                          Valor Unit: {formatCurrency(unitPrice)} • 
                          Total: {formatCurrency(itemTotal)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar: Approval Actions */}
        <div className="finance-sidebar">
          <div className="finance-approval-card">
            <h4>
              <CheckCircle2 size={16} />
              Aprovação Financeira
            </h4>

            <div className="finance-approval-content">
              <div className="finance-approval-info">
                <div className="finance-info-item">
                  <User size={14} />
                  <div>
                    <p className="finance-info-label">Solicitante</p>
                    <p className="finance-info-value">{requisition.requester_name || 'N/A'}</p>
                  </div>
                </div>
                <div className="finance-info-item">
                  <CalendarIcon size={14} />
                  <div>
                    <p className="finance-info-label">Data de Criação</p>
                    <p className="finance-info-value">{formatDate(requisition.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Fornecedor */}
              <div className="finance-approval-section">
                <div className="finance-section-header">
                  <Building2 size={14} />
                  <span className="finance-section-title">Fornecedor</span>
                  <button
                    className="finance-btn-icon"
                    onClick={() => setShowSupplierModal(true)}
                    title={requisition.supplier_name ? 'Editar fornecedor' : 'Adicionar fornecedor'}
                  >
                    {requisition.supplier_name ? <Edit2 size={14} /> : <Plus size={14} />}
                  </button>
                </div>
                {requisition.supplier_name ? (
                  <div className="finance-section-value">
                    {requisition.supplier_name}
                  </div>
                ) : (
                  <div className="finance-section-empty">
                    Nenhum fornecedor vinculado
                  </div>
                )}
              </div>

              {/* Forma de Pagamento */}
              <div className="finance-approval-section">
                <div className="finance-section-header">
                  <CreditCard size={14} />
                  <span className="finance-section-title">Forma de Pagamento</span>
                  <button
                    className="finance-btn-icon"
                    onClick={() => setShowPaymentModal(true)}
                    title={requisition.payment_method ? 'Editar forma de pagamento' : 'Definir forma de pagamento'}
                  >
                    {requisition.payment_method ? <Edit2 size={14} /> : <Plus size={14} />}
                  </button>
                </div>
                {requisition.payment_method ? (
                  <div className="finance-section-value">
                    {requisition.payment_method === 'cash' ? 'À Vista' : `${requisition.payment_installments}x Parcelas`}
                  </div>
                ) : (
                  <div className="finance-section-empty">
                    Não definida
                  </div>
                )}
              </div>

              {/* Preview de Parcelas */}
              {requisition.payment_method === 'installments' && editedPayments.length > 0 && (
                <div className="finance-approval-section finance-payment-dates-section">
                  <InstallmentsPreview
                    installments={editedPayments}
                    totalValue={requisition.total_value || 0}
                    editable
                    minDate={getMinDate()}
                    onInstallmentDateChange={handleInstallmentDateChange}
                  />
                  {hasPaymentDateChanges && (
                    <button
                      type="button"
                      className="finance-btn-save-dates"
                      onClick={handleSavePaymentDates}
                      disabled={savingPaymentDates}
                    >
                      {savingPaymentDates ? (
                        <>
                          <Loader2 className="spinner" size={16} />
                          Salvando...
                        </>
                      ) : (
                        'Salvar datas'
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Vencimento à vista */}
              {requisition.payment_method === 'cash' && (
                <div className="finance-approval-section finance-payment-dates-section">
                  <div className="finance-section-header">
                    <CalendarIcon size={14} />
                    <span className="finance-section-title">Data de Vencimento</span>
                  </div>
                  <CalendarDatePicker
                    value={cashDueDate}
                    minDate={getMinDate()}
                    placeholder="Selecionar vencimento"
                    onChange={setCashDueDate}
                  />
                  {hasPaymentDateChanges && (
                    <button
                      type="button"
                      className="finance-btn-save-dates"
                      onClick={handleSavePaymentDates}
                      disabled={savingPaymentDates || !cashDueDate}
                    >
                      {savingPaymentDates ? (
                        <>
                          <Loader2 className="spinner" size={16} />
                          Salvando...
                        </>
                      ) : (
                        'Salvar data'
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Saldo para Análise */}
              <div className="finance-approval-section">
                <div className="finance-section-header">
                  <Wallet size={14} />
                  <span className="finance-section-title">Saldo para Análise</span>
                </div>
                <div className="finance-balance-selector">
                  <label className="finance-radio-option">
                    <input
                      type="radio"
                      name="balanceType"
                      checked={!useProjectedBalance}
                      onChange={() => {
                        setUseProjectedBalance(false);
                      }}
                    />
                    <span>Atual: {formatCurrency(cashBalance.current || 0)}</span>
                  </label>
                  <label className="finance-radio-option">
                    <input
                      type="radio"
                      name="balanceType"
                      checked={useProjectedBalance}
                      onChange={() => {
                        setUseProjectedBalance(true);
                      }}
                    />
                    <span>Previsto: {formatCurrency(cashBalance.projected || 0)}</span>
                  </label>
                </div>
              </div>

              {/* Switch para Criar Auditoria Automaticamente */}
              <div className="finance-approval-section">
                <div className="finance-section-header">
                  <Package size={14} />
                  <span className="finance-section-title">Criar Auditoria de Estoque</span>
                </div>
                <div className="finance-audit-switch-container">
                  <div className="switch__container">
                    <input
                      id="switch-shadow"
                      className="switch switch--shadow"
                      type="checkbox"
                      checked={createAuditOnApproval}
                      onChange={(e) => setCreateAuditOnApproval(e.target.checked)}
                    />
                    <label htmlFor="switch-shadow"></label>
                  </div>
                  <p className="finance-audit-switch-label">
                    {createAuditOnApproval 
                      ? 'Auditoria será criada automaticamente ao aprovar' 
                      : 'Auditoria não será criada automaticamente'}
                  </p>
                </div>
              </div>

              {showAnalysis && financialAnalysis && (
                <div className="finance-approval-warning">
                  <AlertCircle size={16} />
                  <p>
                    {metrics.riskLevel === 'high' 
                      ? 'Atenção: Esta requisição apresenta alto impacto no fluxo de caixa.'
                      : metrics.riskLevel === 'medium'
                      ? 'Esta requisição apresenta impacto moderado no fluxo de caixa.'
                      : 'Esta requisição apresenta baixo impacto no fluxo de caixa.'}
                  </p>
                </div>
              )}

              <div className="finance-approval-actions">
                <button
                  className="finance-btn-approve"
                  onClick={handleApprove}
                  disabled={isApproving}
                >
                  {isApproving ? (
                    <>
                      <Loader2 className="spinner" size={20} />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Check size={20} />
                      Aprovar Financeiramente
                    </>
                  )}
                </button>

                <button
                  className="finance-btn-reject"
                  onClick={handleReject}
                  disabled={isApproving}
                >
                  <X size={20} />
                  Rejeitar
                </button>
              </div>

              <div className="finance-approval-note">
                <Info size={16} />
                <p>
                  A aprovação financeira libera o pagamento e avança a requisição para o estágio de rastreio.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toast
        message={typeof toast.message === 'string' ? toast.message : (toast.message?.message || JSON.stringify(toast.message) || 'Erro desconhecido')}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' })}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
        }}
        confirmText="Confirmar"
        cancelText="Cancelar"
      />

      {/* Payment Method Modal - Estilo Prospecting */}
      {showPaymentModal && (
        <div className="prospecting-modal-overlay">
          <div className="prospecting-modal-backdrop" onClick={() => setShowPaymentModal(false)} />
          <div className="prospecting-modal-content" style={{ maxWidth: '36rem' }}>
            <div className="prospecting-modal-header">
              <div className="prospecting-modal-header-content">
                <div className="prospecting-modal-icon-wrapper">
                  <CreditCard size={24} />
                </div>
                <div>
                  <h3 className="prospecting-modal-title">Forma de Pagamento</h3>
                  <p className="prospecting-modal-subtitle">Definir Condições de Pagamento</p>
                </div>
              </div>
              <button 
                type="button" 
                onClick={() => setShowPaymentModal(false)}
                className="prospecting-modal-close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="prospecting-modal-body">
              {paymentErrors.general && (
                <div style={{ 
                  padding: '1rem', 
                  marginBottom: '1.5rem', 
                  background: 'rgba(239, 68, 68, 0.1)', 
                  color: '#ef4444',
                  borderRadius: '1rem',
                  fontSize: '0.75rem'
                }}>
                  {paymentErrors.general}
                </div>
              )}

              <div className="prospecting-form-group">
                <label className="prospecting-form-label">Forma de Pagamento *</label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{
                    flex: 1,
                    padding: '1rem',
                    background: paymentMethod === 'cash' ? 'rgba(34, 197, 94, 0.1)' : 'var(--bg-secondary)',
                    border: `2px solid ${paymentMethod === 'cash' ? '#22c55e' : 'transparent'}`,
                    borderRadius: '2rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={paymentMethod === 'cash'}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setInstallmentsPreview(null);
                      }}
                      style={{ display: 'none' }}
                    />
                    <DollarSign size={20} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>À Vista</span>
                  </label>
                  <label style={{
                    flex: 1,
                    padding: '1rem',
                    background: paymentMethod === 'installments' ? 'rgba(34, 197, 94, 0.1)' : 'var(--bg-secondary)',
                    border: `2px solid ${paymentMethod === 'installments' ? '#22c55e' : 'transparent'}`,
                    borderRadius: '2rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="installments"
                      checked={paymentMethod === 'installments'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      style={{ display: 'none' }}
                    />
                    <CreditCard size={20} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>Parcelado</span>
                  </label>
                </div>
              </div>

              {paymentMethod === 'installments' && (
                <>
                  <div className="prospecting-form-grid">
                    <div className="prospecting-form-group">
                      <label className="prospecting-form-label">Número de Parcelas *</label>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={installments}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          setInstallments(Math.min(12, Math.max(1, value)));
                        }}
                        className="prospecting-form-input"
                        style={paymentErrors.installments ? { border: '2px solid #ef4444' } : {}}
                      />
                      {paymentErrors.installments && (
                        <span style={{ fontSize: '0.625rem', color: '#ef4444', marginTop: '0.25rem' }}>
                          {paymentErrors.installments}
                        </span>
                      )}
                    </div>
                    <div className="prospecting-form-group">
                      <label className="prospecting-form-label">Intervalo (dias) *</label>
                      <input
                        type="number"
                        min="7"
                        max="90"
                        value={intervalDays}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 30;
                          setIntervalDays(Math.min(90, Math.max(7, value)));
                        }}
                        className="prospecting-form-input"
                        style={paymentErrors.intervalDays ? { border: '2px solid #ef4444' } : {}}
                      />
                      {paymentErrors.intervalDays && (
                        <span style={{ fontSize: '0.625rem', color: '#ef4444', marginTop: '0.25rem' }}>
                          {paymentErrors.intervalDays}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="prospecting-form-group">
                    <label className="prospecting-form-label">Primeiro Vencimento *</label>
                    <input
                      type="date"
                      min={getMinDate()}
                      value={firstDueDate}
                      onChange={(e) => setFirstDueDate(e.target.value)}
                      className="prospecting-form-input"
                      style={paymentErrors.firstDueDate ? { border: '2px solid #ef4444' } : {}}
                    />
                    {paymentErrors.firstDueDate && (
                      <span style={{ fontSize: '0.625rem', color: '#ef4444', marginTop: '0.25rem' }}>
                        {paymentErrors.firstDueDate}
                      </span>
                    )}
                  </div>

                  {calculating && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                      <Loader2 className="spinner" size={18} />
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Calculando parcelas...</span>
                    </div>
                  )}

                  {installmentsPreview && !calculating && (
                    <div style={{
                      padding: '1.5rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: '2rem',
                      marginBottom: '1.5rem'
                    }}>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: 900, marginBottom: '1rem' }}>Preview das Parcelas</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {installmentsPreview.installments?.map((inst, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.75rem',
                            background: 'var(--bg-primary)',
                            borderRadius: '1rem'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <CalendarIcon size={14} />
                              <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                {inst.installmentNumber}ª Parcela - {formatDate(inst.dueDate)}
                              </span>
                            </div>
                            <span style={{ fontSize: '0.875rem', fontWeight: 900 }}>
                              {formatCurrency(inst.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {paymentMethod === 'cash' && (
                <div style={{
                  padding: '1.5rem',
                  background: 'rgba(34, 197, 94, 0.1)',
                  borderRadius: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <DollarSign size={24} style={{ color: '#22c55e' }} />
                  <div>
                    <p style={{ fontSize: '0.625rem', fontWeight: 900, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: 0 }}>
                      Pagamento à Vista
                    </p>
                    <p style={{ fontSize: '1.125rem', fontWeight: 900, color: 'var(--text-primary)', margin: '0.25rem 0 0 0' }}>
                      {formatCurrency(getTotalValue())}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="prospecting-modal-footer">
              <button 
                type="button"
                onClick={() => setShowPaymentModal(false)}
                className="prospecting-btn"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              >
                Cancelar
              </button>
              <button 
                type="button"
                onClick={handleSavePaymentMethod}
                className="prospecting-btn prospecting-btn-primary"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Modal - Estilo Prospecting */}
      {showSupplierModal && (
        <div className="prospecting-modal-overlay">
          <div className="prospecting-modal-backdrop" onClick={() => setShowSupplierModal(false)} />
          <div className="prospecting-modal-content" style={{ maxWidth: '36rem' }}>
            <div className="prospecting-modal-header">
              <div className="prospecting-modal-header-content">
                <div className="prospecting-modal-icon-wrapper">
                  <Building2 size={24} />
                </div>
                <div>
                  <h3 className="prospecting-modal-title">Fornecedor</h3>
                  <p className="prospecting-modal-subtitle">Cadastrar ou Vincular Fornecedor</p>
                </div>
              </div>
              <button 
                type="button" 
                onClick={() => setShowSupplierModal(false)}
                className="prospecting-modal-close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="prospecting-modal-body">
              {foundSupplier && (
                <div style={{
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid #10b981',
                  borderRadius: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#10b981'
                }}>
                  <CheckCircle2 size={16} />
                  <strong style={{ fontSize: '0.75rem' }}>Fornecedor encontrado:</strong>
                  <span style={{ fontSize: '0.75rem' }}>{foundSupplier.name}</span>
                </div>
              )}

              <div className="prospecting-form-group">
                <label className="prospecting-form-label">CNPJ/CPF</label>
                <input
                  type="text"
                  value={supplierData.cnpj}
                  onChange={(e) => handleCnpjChange(e.target.value)}
                  placeholder="00.000.000/0000-00 ou 000.000.000-00"
                  className="prospecting-form-input"
                />
                {searchingSupplier && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <Loader2 className="spinner" size={14} />
                    <span style={{ fontSize: '0.625rem', color: 'var(--text-secondary)' }}>
                      Buscando fornecedor...
                    </span>
                  </div>
                )}
              </div>

              <div className="prospecting-form-group">
                <label className="prospecting-form-label">Nome do Fornecedor *</label>
                <input
                  type="text"
                  value={supplierData.name}
                  onChange={(e) => setSupplierData({ ...supplierData, name: e.target.value })}
                  placeholder="Nome completo ou razão social"
                  required
                  disabled={!!foundSupplier}
                  className="prospecting-form-input"
                  style={foundSupplier ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>
            <div className="prospecting-modal-footer">
              <button 
                type="button"
                onClick={() => {
                  setShowSupplierModal(false);
                  setSupplierData({ name: '', cnpj: '' });
                  setFoundSupplier(null);
                }}
                className="prospecting-btn"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              >
                Cancelar
              </button>
              <button 
                type="button"
                onClick={handleSaveSupplier}
                className="prospecting-btn prospecting-btn-primary"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceApprovalPage;
